import {
  ApplicationConfig,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
  isDevMode,
  SecurityContext,
} from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import {
  provideRouter,
  withRouterConfig,
  withComponentInputBinding,
} from '@angular/router';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withXhr,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideTranslateService,
  provideMissingTranslationHandler,
  TranslateService,
} from '@ngx-translate/core';
import { SelfHealingMissingTranslationHandler } from './core/i18n/self-healing-missing-translation.handler';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  firstValueFrom,
  of,
  timeout,
  TimeoutError,
  catchError,
  map,
  defaultIfEmpty,
} from 'rxjs';
import { provideMarkdown, MARKED_OPTIONS, SANITIZE } from 'ngx-markdown';
import { secureMarkedOptionsFactory } from './shared/markdown/secure-marked-options';
import {
  baseurlInterceptorFactory,
  jwtInterceptorFactory,
  subIdInterceptorFactory,
  retryInterceptorFactory,
  loaderInterceptorFactory,
  ENVIRONMENT,
  INTERCEPTOR_CONFIG,
  provideAuth,
} from '@pa-angular/pa-fe-boilerplate';

import { routes } from './app.routes';
import {
  EnvironmentService,
  Environment,
} from './root-services/environment.service';
import { AppConfigService } from './root-services/app-config.service';
import { PwaUpdateService } from './root-services/pwa-update.service';
import { errorInterceptor } from './api/interceptors/error.interceptor';
import { SubscriptionBypassInterceptor } from './api/interceptors/subscription-bypass.interceptor';
import { InterceptorConfig } from '@pa-angular/pa-fe-boilerplate';

/**
 * Maximum time bootstrap will wait for the default-language translations before
 * rendering anyway. Keeps a slow/failed i18n fetch from hanging the whole app —
 * a brief splash beats a permanently frozen bootstrap.
 *
 * Deliberately a compile-time constant, not sourced from `EnvironmentService`:
 * it guards the very bootstrap that loads `env.json`, so reading it from there
 * would be circular. Exported so it can be tuned in one place and referenced by
 * tests. 3s balances "don't make cold clients stare at a splash" against
 * "give a slow network a real chance to deliver en.json before degrading".
 */
export const TRANSLATIONS_INIT_TIMEOUT_MS = 3000;

/**
 * APP_INITIALIZER factory that loads the default-language translations before
 * first render.
 *
 * Rendering is not otherwise gated on translation loading, and 23 sites call
 * `translate.instant()` synchronously. If a render beats the `en.json` fetch,
 * `instant()` returns the raw key permanently until reload — the "translations
 * show as keys on first load" bug. Awaiting `use('en')` here closes that race.
 *
 * The await is bounded by {@link TRANSLATIONS_INIT_TIMEOUT_MS} via the RxJS
 * `timeout` operator: on timeout or error we log and continue (emitting once)
 * so bootstrap always proceeds (degraded, showing raw keys, rather than
 * hanging). Using the operator — rather than a bare `setTimeout` racing the
 * promise — means a genuinely hung `use('en')` is unsubscribed when the timeout
 * fires instead of leaking past bootstrap. `defaultIfEmpty` guards the case
 * where the loader completes without emitting (avoids a misleading
 * `firstValueFrom` EmptyError). For returning clients the service worker serves
 * `en.json` from CacheStorage instantly, so only a cold client waits on the
 * network — which is the desired trade-off.
 */
export function translationsInitializerFactory(): Promise<void> {
  const translate = inject(TranslateService);
  return firstValueFrom(
    translate.use('en').pipe(
      timeout({ first: TRANSLATIONS_INIT_TIMEOUT_MS }),
      catchError((error: unknown) => {
        if (error instanceof TimeoutError) {
          console.warn(
            '[app.config] Translation load exceeded timeout; rendering without awaiting i18n.',
          );
        } else {
          console.error('[app.config] Failed to load translations:', error);
        }
        return of(undefined);
      }),
      map(() => undefined),
      defaultIfEmpty(undefined),
    ),
  );
}

/**
 * Application configuration
 *
 * Providers:
 * - Zone change detection with event coalescing
 * - Router with dynamic routes (configured via DynamicRouteService)
 * - HTTP client with interceptor chain
 * - Animations (async)
 * - ngx-translate for internationalization
 * - ngx-markdown with Prism.js, Mermaid, and KaTeX support
 * - App initialization (loads environment and configuration)
 *
 * HTTP Interceptor Chain Order (class-based via HTTP_INTERCEPTORS):
 * 1. SubscriptionBypassInterceptor - Bypass SubId blocking for subscription/entitlement fetching (MUST run first)
 * 2. baseurlInterceptorFactory - Prepend backend base URL
 * 3. jwtInterceptorFactory - Add JWT Authorization header
 * 4. subIdInterceptorFactory - Add subscription-id header (bypassed by SubscriptionBypassInterceptor)
 * 5. retryInterceptorFactory - Retry failed requests
 * 6. loaderInterceptorFactory - Track loading state
 *
 * HTTP Interceptor Chain Order (function-based via withInterceptors):
 * 1. errorInterceptor - Handle HTTP errors with user-friendly messages
 *
 * Note: SubscriptionBypassInterceptor works around SubId interceptor blocking issue
 * See: docs/bmad-archive/implementation-artifacts/library-issues/subid-interceptor-subscription-fetch-blocking.md
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
    ),
    provideHttpClient(
      withXhr(),
      withInterceptors([errorInterceptor]),
      withInterceptorsFromDi(),
    ),
    provideAnimations(),
    // ngx-translate (v18). Translation files load from ./assets/i18n/ via the
    // built-in HTTP loader configured with `useHttpBackend: true`, which builds
    // its `HttpClient` on `HttpBackend` and so deliberately bypasses the DI
    // interceptor chain. Two reasons:
    // 1. These are static local assets — they must not traverse the backend
    //    interceptors (baseurl would prepend the API host to the asset path;
    //    jwt / subId add headers that are meaningless for a static file).
    // 2. `translationsInitializerFactory` fetches `en.json` from an
    //    APP_INITIALIZER, making it the app's first HttpClient request. Routing
    //    it through DI interceptors would force `HTTP_INTERCEPTORS` to hydrate
    //    mid-bootstrap while the injector tree (Router, EnvironmentService →
    //    DynamicRouteService → Router) is still being constructed, re-entering
    //    the same token → NG0200 circular dependency. `useHttpBackend` avoids
    //    that (the v18 replacement for the previous hand-built HttpBackend loader).
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
        useHttpBackend: true,
      }),
      missingTranslationHandler: provideMissingTranslationHandler(
        SelfHealingMissingTranslationHandler,
      ),
    }),
    provideMarkdown({
      // Defense-in-depth: Angular sanitizer strips dangerous HTML (scripts,
      // event handlers). The custom renderer below additionally entity-encodes
      // ALL raw HTML from model output so it renders as markdown only — closing
      // HTML injection on the agent/model bubble (pentest Finding 01, CWE-74).
      sanitize: {
        provide: SANITIZE,
        useValue: SecurityContext.HTML,
      },
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: secureMarkedOptionsFactory,
      },
      // Note: Mermaid and KaTeX are handled by custom loader services for lazy loading
      // - Mermaid: ngx-markdown creates <code class="language-mermaid"> blocks, we render SVG manually
      // - KaTeX: ngx-markdown leaves $...$ as plain text, we detect and render math manually
    }),
    {
      provide: ENVIRONMENT,
      useFactory: () => {
        const envService = inject(EnvironmentService);
        return envService.env();
      },
    },
    {
      provide: INTERCEPTOR_CONFIG,
      useFactory: () => {
        const configService = inject(AppConfigService);
        return configService.interceptorConfig();
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SubscriptionBypassInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (env: Environment, config: InterceptorConfig) =>
        baseurlInterceptorFactory(env, config),
      deps: [ENVIRONMENT, INTERCEPTOR_CONFIG],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (env: Environment, config: InterceptorConfig) =>
        jwtInterceptorFactory(env, config),
      deps: [ENVIRONMENT, INTERCEPTOR_CONFIG],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (env: Environment, config: InterceptorConfig) =>
        subIdInterceptorFactory(env, config),
      deps: [ENVIRONMENT, INTERCEPTOR_CONFIG],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: () => retryInterceptorFactory(),
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (env: Environment, config: InterceptorConfig) =>
        loaderInterceptorFactory(env, config),
      deps: [ENVIRONMENT, INTERCEPTOR_CONFIG],
      multi: true,
    },
    // Auth strategy initialization (from boilerplate 11.1).
    // provideAuth() and EnvironmentService.init() both run as APP_INITIALIZERs
    // in parallel (Angular Promise.all). They are independent — neither depends
    // on the other's output during initialization.
    provideAuth(),
    provideAppInitializer(() => {
      const initializerFn = () => {
        const envService = inject(EnvironmentService);
        return envService.init();
      };
      return initializerFn();
    }),
    // Load default-language translations before first render so synchronous
    // `translate.instant()` calls never return raw keys. Runs in parallel with
    // the other initializers — i18n is static (./assets/i18n/), independent of
    // EnvironmentService/backend base URL. Bounded + log-and-continue on failure.
    provideAppInitializer(() => translationsInitializerFactory()),
    // PWA service worker: registered only in production builds. `enabled` is
    // gated on !isDevMode() so `ng serve`/local builds never install a SW and
    // its caching (which is a common source of "stuck on old version" during
    // development). `registerWhenStable:30000` defers registration until the
    // app is stable (or 30s elapse) so it does not compete with initial load.
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Start listening for downloaded updates and prompt the user to reload.
    provideAppInitializer(() => inject(PwaUpdateService).init()),
  ],
};
